import { isAxiosError } from "axios";
import { Form, FormikProvider, useFormik } from "formik";
import {
    REPORT_SETTINGS_TEMPLATE_DESCRIPTION_MAX_LENGTH,
    REPORT_SETTINGS_TEMPLATE_NAME_MAX_LENGTH,
} from "@common/constants/reports/report-settings-template";
import { createFormikFields } from "@components/forms/fields/createFormikFields";
import { Button } from "@design-system/Button/Button";
import { ButtonWithLoader } from "@design-system/Button/ButtonWithLoader";
import { HStack } from "@design-system/HStack/HStack";
import {
    Modal,
    StandardModalBody,
    StandardModalFooter,
    StandardModalHeader,
} from "@design-system/Modal/Modal";
import { VStack } from "@design-system/VStack/VStack";
import { submitHelper } from "@helpers/form";
import { ReportTemplateDetailsValues } from "./reportTemplateDetailsForm";

const fields = createFormikFields<ReportTemplateDetailsValues>();

const SAVE_FAILURE_MESSAGE = "Couldn't save the template. Try again.";

const getErrorMessage = (error: Error) =>
    isAxiosError(error) && typeof error.response?.data?.message === "string"
        ? error.response.data.message
        : SAVE_FAILURE_MESSAGE;

interface Props {
    show: boolean;
    title: string;
    submitLabel: string;
    initialValues: ReportTemplateDetailsValues;
    onSubmit: (values: ReportTemplateDetailsValues) => Promise<void>;
    onHide: () => void;
}

export const ReportTemplateDetailsModal = ({
    show,
    title,
    submitLabel,
    initialValues,
    onSubmit,
    onHide,
}: Props) => {
    const formik = useFormik<ReportTemplateDetailsValues>({
        initialValues,
        onSubmit: submitHelper({
            loading: false,
            handler: async (values) => {
                await onSubmit(values);
                handleHide();
            },
            failureHandler: async (error, _values, formikHelpers) => {
                void formikHelpers.setFieldError(
                    "name",
                    getErrorMessage(error),
                );
            },
        }),
    });

    const handleHide = () => {
        formik.resetForm();
        onHide();
    };

    return (
        <Modal
            show={show}
            onClose={formik.isSubmitting ? undefined : handleHide}
            size="lg"
        >
            <StandardModalHeader>{title}</StandardModalHeader>
            <FormikProvider value={formik}>
                <Form>
                    <StandardModalBody>
                        <VStack gap={16}>
                            <fields.Text
                                name="name"
                                label="Template name"
                                size="lg"
                                maxLength={
                                    REPORT_SETTINGS_TEMPLATE_NAME_MAX_LENGTH
                                }
                                autoFocus
                            />
                            <fields.TextArea
                                name="description"
                                label="Description"
                                optional
                                size="lg"
                                autoGrow
                                minRows={2}
                                maxLength={
                                    REPORT_SETTINGS_TEMPLATE_DESCRIPTION_MAX_LENGTH
                                }
                            />
                        </VStack>
                    </StandardModalBody>
                    <StandardModalFooter>
                        <HStack gap={12} justify="end">
                            <Button
                                type="button"
                                variant="secondary"
                                size="xl"
                                onClick={handleHide}
                                disabled={formik.isSubmitting}
                            >
                                Cancel
                            </Button>
                            <ButtonWithLoader
                                type="submit"
                                variant="primary"
                                size="xl"
                                loading={formik.isSubmitting}
                                disabled={formik.values.name.trim() === ""}
                                hotKey="mod+enter"
                                hotKeyDisplayPosition="inline"
                                hotKeyOptions={{
                                    description: submitLabel,
                                    preventDefault: true,
                                    enableOnFormTags: true,
                                }}
                            >
                                {submitLabel}
                            </ButtonWithLoader>
                        </HStack>
                    </StandardModalFooter>
                </Form>
            </FormikProvider>
        </Modal>
    );
};
